import React, { useState } from 'react'
import { PageHeader } from '@/components/layout'
import { useAuth } from '@/hooks/useAuth'
import { usePendingWithdrawals as useWithdrawals, useApproveWithdrawal, useRejectWithdrawal } from '@/hooks/useWithdrawal'

type WithdrawalStatus = 'all' | 'pending' | 'approved' | 'rejected' | 'completed'

export const AdminWithdrawalsPage: React.FC = () => {
    const { user } = useAuth()
    const { data: withdrawals, isLoading } = useWithdrawals()
    const approveWithdrawal = useApproveWithdrawal()
    const rejectWithdrawal = useRejectWithdrawal()
    const [statusFilter, setStatusFilter] = useState<WithdrawalStatus>('all')
    const [showRejectModal, setShowRejectModal] = useState(false)
    const [selectedWithdrawalId, setSelectedWithdrawalId] = useState<string | null>(null)
    const [rejectReason, setRejectReason] = useState('')
    const [actionError, setActionError] = useState<string | null>(null)

    const filteredWithdrawals = withdrawals?.filter((w: any) => {
        return statusFilter === 'all' || w.status === statusFilter
    }) || []

    const getStatusBadgeColor = (status: string) => {
        switch (status) {
            case 'pending': return 'bg-amber-500 text-white'
            case 'approved': return 'bg-indigo-500 text-white'
            case 'rejected': return 'bg-rose-500 text-white'
            case 'completed': return 'bg-emerald-500 text-white'
            default: return 'bg-slate-400 text-white'
        }
    }

    const pendingCount = withdrawals?.filter((w: any) => w.status === 'pending')?.length || 0
    const totalPending = withdrawals?.filter((w: any) => w.status === 'pending')?.reduce((acc: number, w: any) => acc + (w.amount || 0), 0) || 0

    // Calculate processed today count
    const processedToday = withdrawals?.filter((w: any) => {
        const today = new Date().toDateString()
        const completedDate = w.completedAt ? new Date(w.completedAt).toDateString() : null
        return w.status === 'completed' && completedDate === today
    })?.length || 0

    const handleApprove = (withdrawalId: string) => {
        if (!user?.id) {
            setActionError('Admin not authenticated')
            return
        }

        setActionError(null)
        approveWithdrawal.mutate({
            id: withdrawalId,
            adminId: user.id,
        }, {
            onError: (error: any) => {
                setActionError(error?.response?.data?.error?.message || 'Failed to approve withdrawal')
            }
        })
    }

    const openRejectModal = (withdrawalId: string) => {
        setSelectedWithdrawalId(withdrawalId)
        setRejectReason('')
        setShowRejectModal(true)
    }

    const handleReject = () => {
        if (!user?.id || !selectedWithdrawalId) {
            setActionError('Admin not authenticated or no withdrawal selected')
            return
        }

        if (!rejectReason.trim()) {
            setActionError('Please provide a reason for rejection')
            return
        }

        setActionError(null)
        rejectWithdrawal.mutate({
            id: selectedWithdrawalId,
            adminId: user.id,
            reason: rejectReason.trim(),
        }, {
            onSuccess: () => {
                setShowRejectModal(false)
                setSelectedWithdrawalId(null)
                setRejectReason('')
            },
            onError: (error: any) => {
                setActionError(error?.response?.data?.error?.message || 'Failed to reject withdrawal')
            }
        })
    }

    const closeRejectModal = () => {
        setShowRejectModal(false)
        setSelectedWithdrawalId(null)
        setRejectReason('')
        setActionError(null)
    }

    return (
        <div>
            <PageHeader
                title="Withdrawals"
                subtitle="Review and process withdrawal requests"
            />

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="rounded-lg bg-white dark:bg-surface-card border border-gray-200 dark:border-white/5 p-5">
                    <div className="flex items-center gap-2 mb-2">
                        <span className="material-symbols-outlined text-yellow-400 text-[20px]">pending</span>
                        <p className="text-gray-500 dark:text-text-muted text-xs font-medium uppercase">Pending Requests</p>
                    </div>
                    <p className="text-gray-900 dark:text-white text-2xl font-bold">{pendingCount}</p>
                </div>
                <div className="rounded-lg bg-white dark:bg-surface-card border border-gray-200 dark:border-white/5 p-5">
                    <div className="flex items-center gap-2 mb-2">
                        <span className="material-symbols-outlined text-primary text-[20px]">payments</span>
                        <p className="text-gray-500 dark:text-text-muted text-xs font-medium uppercase">Total Pending Amount</p>
                    </div>
                    <p className="text-gray-900 dark:text-white text-2xl font-bold">Rp {totalPending.toLocaleString('id-ID')}</p>
                </div>
                <div className="rounded-lg bg-white dark:bg-surface-card border border-gray-200 dark:border-white/5 p-5">
                    <div className="flex items-center gap-2 mb-2">
                        <span className="material-symbols-outlined text-green-400 text-[20px]">check_circle</span>
                        <p className="text-gray-500 dark:text-text-muted text-xs font-medium uppercase">Processed Today</p>
                    </div>
                    <p className="text-gray-900 dark:text-white text-2xl font-bold">{processedToday}</p>
                </div>
            </div>

            {/* Status Filters */}
            <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2 md:pb-0">
                {(['all', 'pending', 'approved', 'rejected', 'completed'] as WithdrawalStatus[]).map((status) => (
                    <button
                        key={status}
                        onClick={() => setStatusFilter(status)}
                        className={`px-4 py-2 rounded-lg text-xs font-bold uppercase transition-colors whitespace-nowrap ${statusFilter === status
                            ? 'bg-primary text-white'
                            : 'bg-white dark:bg-surface-card text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white border border-gray-200 dark:border-white/5'
                            }`}
                    >
                        {status}
                    </button>
                ))}
            </div>

            {/* Withdrawals Table */}
            <div className="rounded-lg bg-white dark:bg-surface-card border border-gray-200 dark:border-white/5 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[500px]">
                        <thead>
                            <tr className="border-b border-gray-200 dark:border-white/5">
                                <th className="text-left px-5 py-4 text-xs font-bold uppercase text-gray-500 dark:text-text-muted">User</th>
                                <th className="text-left px-5 py-4 text-xs font-bold uppercase text-gray-500 dark:text-text-muted">Amount</th>
                                <th className="text-left px-5 py-4 text-xs font-bold uppercase text-gray-500 dark:text-text-muted hidden md:table-cell">Bank</th>
                                <th className="text-left px-5 py-4 text-xs font-bold uppercase text-gray-500 dark:text-text-muted">Status</th>
                                <th className="text-left px-5 py-4 text-xs font-bold uppercase text-gray-500 dark:text-text-muted hidden md:table-cell">Requested</th>
                                <th className="text-right px-5 py-4 text-xs font-bold uppercase text-gray-500 dark:text-text-muted">Actions</th>
                            </tr>
                        </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-white/5">
                        {isLoading ? (
                            <tr>
                                <td colSpan={6} className="px-5 py-12 text-center text-gray-500 dark:text-text-muted">Loading...</td>
                            </tr>
                        ) : filteredWithdrawals.length === 0 ? (
                            <tr>
                                <td colSpan={6} className="px-5 py-12 text-center text-gray-500 dark:text-text-muted">No withdrawal requests</td>
                            </tr>
                        ) : (
                            filteredWithdrawals.map((w: any) => (
                                <tr key={w.id} className="hover:bg-gray-100 dark:hover:bg-white/5 transition-colors">
                                    <td className="px-5 py-4">
                                        <div className="flex items-center gap-3">
                                            <div className="size-10 rounded-full bg-gradient-to-br from-primary/40 to-primary/20 flex items-center justify-center">
                                                <span className="text-sm font-bold text-red-300">{w.userName?.charAt(0) || 'U'}</span>
                                            </div>
                                            <div>
                                                <p className="text-sm font-bold text-gray-900 dark:text-white">{w.userName || 'User'}</p>
                                                <p className="text-xs text-gray-500 dark:text-text-muted">{w.userRole || '-'}</p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-5 py-4 text-sm font-bold text-gray-900 dark:text-white">
                                        Rp {(w.amount || 0).toLocaleString('id-ID')}
                                    </td>
                                    <td className="px-5 py-4 text-sm text-gray-500 dark:text-text-muted hidden md:table-cell">
                                        {w.bankName && w.accountNumber
                                            ? `${w.bankName} - ${w.accountNumber}`
                                            : '-'}
                                    </td>
                                    <td className="px-5 py-4">
                                        <span className={`inline-flex px-2.5 py-1 rounded-full text-[10px] font-bold uppercase ${getStatusBadgeColor(w.status)}`}>
                                            {w.status}
                                        </span>
                                    </td>
                                    <td className="px-5 py-4 text-sm text-gray-500 dark:text-text-muted hidden md:table-cell">
                                        {w.createdAt ? new Date(w.createdAt).toLocaleDateString('id-ID') : '-'}
                                    </td>
                                    <td className="px-5 py-4">
                                        {w.status === 'pending' && (
                                            <div className="flex items-center justify-end gap-2">
                                                <button
                                                    onClick={() => handleApprove(w.id)}
                                                    disabled={approveWithdrawal.isPending}
                                                    className="px-3 py-1.5 rounded-lg bg-emerald-500 text-white text-xs font-bold hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    {approveWithdrawal.isPending ? 'Processing...' : 'Approve'}
                                                </button>
                                                <button
                                                    onClick={() => openRejectModal(w.id)}
                                                    disabled={rejectWithdrawal.isPending}
                                                    className="px-3 py-1.5 rounded-lg bg-rose-500 text-white text-xs font-bold hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    Reject
                                                </button>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))
                        )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Error Message */}
            {actionError && (
                <div className="fixed bottom-4 right-4 bg-red-500/20 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg text-sm">
                    {actionError}
                    <button onClick={() => setActionError(null)} className="ml-3 text-red-300 hover:text-gray-900 dark:hover:text-white">
                        Dismiss
                    </button>
                </div>
            )}

            {/* Reject Modal */}
            {showRejectModal && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50" onClick={closeRejectModal}>
                    <div className="bg-gray-50 dark:bg-surface-dark rounded-lg p-6 w-full max-w-md border border-gray-200 dark:border-white/10" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-lg font-bold text-gray-900 dark:text-white">Reject Withdrawal</h2>
                            <button onClick={closeRejectModal} className="size-8 rounded-lg hover:bg-gray-200 dark:hover:bg-white/10 flex items-center justify-center transition-colors">
                                <span className="material-symbols-outlined text-gray-500 dark:text-gray-400">close</span>
                            </button>
                        </div>
                        <div className="mb-4">
                            <label className="block text-xs font-medium text-gray-500 dark:text-text-muted mb-2">Reason for Rejection</label>
                            <textarea
                                value={rejectReason}
                                onChange={(e) => setRejectReason(e.target.value)}
                                placeholder="Enter reason for rejecting this withdrawal request..."
                                rows={4}
                                className="w-full px-4 py-2.5 bg-white dark:bg-surface-card border border-gray-200 dark:border-white/10 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-primary/50 resize-none"
                            />
                        </div>
                        <div className="flex gap-3">
                            <button onClick={closeRejectModal} className="flex-1 py-2.5 rounded-lg bg-gray-100 dark:bg-white/5 text-sm font-medium text-gray-900 dark:text-white hover:bg-gray-200 dark:hover:bg-white/10 transition-colors">
                                Cancel
                            </button>
                            <button
                                onClick={handleReject}
                                disabled={!rejectReason.trim() || rejectWithdrawal.isPending}
                                className="flex-1 py-2.5 rounded-lg bg-red-500 text-white text-sm font-bold hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {rejectWithdrawal.isPending ? 'Rejecting...' : 'Reject Withdrawal'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default AdminWithdrawalsPage
